import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import { StatusBadge, OverallBadge } from "@/components/StatusBadge";
import UploadDialog from "@/components/UploadDialog";
import RoleSelectionDialog from "@/components/RoleSelectionDialog";
import NMCPanel from "@/components/NMCPanel";
import AlertsPanel from "@/components/AlertsPanel";
import WorkerAvatar from "@/components/WorkerAvatar";
import PersonalDetailsForm, { EMPTY_PERSONAL_DETAILS } from "@/components/PersonalDetailsForm";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { UploadCloud, AlertCircle, FileText, RefreshCcw, CalendarClock } from "lucide-react";
import { roleLabel, formatDate } from "@/lib/checklist";

export default function WorkerDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadItem, setUploadItem] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cl, al] = await Promise.all([api.get("/checklist/me"), api.get("/alerts")]);
      setData(cl.data);
      setAlerts(al.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && user.role === "worker" && user.worker_role) load();
  }, [user, load]);

  const showRoleSelection = user && user.role === "worker" && !user.worker_role;

  return (
    <AppLayout>
      <RoleSelectionDialog open={!!showRoleSelection} />

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <WorkerAvatar workerId={user?.id} name={user?.name} size="lg" bust={data?.summary?.approved} />
          <div>
            <div className="label-caps text-slate-500">Your checklist</div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mt-1">
              Hello, {user?.name?.split(" ")[0]}
            </h1>
            <p className="text-slate-600 mt-1.5">
              Role: <span className="font-semibold text-slate-800">{roleLabel(user?.worker_role)}</span>
            </p>
          </div>
        </div>
        {data?.summary && <OverallBadge overall={data.summary.overall} testid="worker-overall" />}
      </div>

      {alerts.length > 0 && (
        <div className="mt-6">
          <AlertsPanel alerts={alerts} />
        </div>
      )}

      {data?.summary && (
        <div className="mt-8 rounded-xl border border-slate-200 bg-white p-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="label-caps text-slate-500">Overall progress</div>
              <div className="font-heading text-3xl font-bold text-slate-900 mt-1">
                {data.summary.progress}%
              </div>
              <div className="text-sm text-slate-600 mt-1">
                {data.summary.approved} of {data.summary.total} items approved and in-date
              </div>
            </div>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-center">
              {[
                { k: "approved", label: "Approved", color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
                { k: "expiring_soon", label: "Expiring", color: "text-amber-700 bg-amber-50 border-amber-200" },
                { k: "expired", label: "Expired", color: "text-rose-700 bg-rose-50 border-rose-300" },
                { k: "pending", label: "In review", color: "text-amber-700 bg-amber-50 border-amber-200" },
                { k: "rejected", label: "Rejected", color: "text-rose-700 bg-rose-50 border-rose-200" },
                { k: "missing", label: "Missing", color: "text-slate-700 bg-slate-50 border-slate-200" },
              ].map((s) => (
                <div key={s.k} className={`rounded-lg border px-3 py-2 ${s.color}`}>
                  <div className="font-heading text-lg font-bold" data-testid={`worker-count-${s.k}`}>
                    {data.summary[s.k] ?? 0}
                  </div>
                  <div className="text-[0.6rem] uppercase tracking-widest mt-0.5">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-5">
            <Progress value={data.summary.progress} className="h-2" />
          </div>
        </div>
      )}

      {/* NMC panel for nurses */}
      {user?.worker_role === "registered_nurse" && data?.nmc && (
        <div className="mt-6">
          <NMCPanel worker={user} nmc={data.nmc} canReview={false} onChanged={load} />
        </div>
      )}

      {user?.id && (
        <WorkerPersonalDetailsSection worker={{ ...user, personal_details: data?.worker?.personal_details }} onSaved={load} />
      )}

      <div className="mt-8">
        <Tabs defaultValue="all">
          <div className="flex items-center justify-between gap-4">
            <TabsList className="bg-white border border-slate-200 h-auto p-1">
              <TabsTrigger value="all" data-testid="tab-all" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white h-8 px-4">All items</TabsTrigger>
              <TabsTrigger value="action" data-testid="tab-action" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white h-8 px-4">Action required</TabsTrigger>
              <TabsTrigger value="approved" data-testid="tab-approved" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white h-8 px-4">Approved</TabsTrigger>
            </TabsList>
            <Button variant="ghost" size="sm" onClick={load} data-testid="reload-checklist">
              <RefreshCcw className="h-4 w-4 mr-1.5" /> Refresh
            </Button>
          </div>

          {[
            { key: "all", filter: (i) => i.special_type !== "nmc_pin" },
            { key: "action", filter: (i) => i.special_type !== "nmc_pin" && ["rejected", "missing", "expired", "expiring_soon"].includes(i.status) },
            { key: "approved", filter: (i) => i.special_type !== "nmc_pin" && i.status === "approved" },
          ].map((tab) => (
            <TabsContent key={tab.key} value={tab.key} className="mt-4">
              <div className="space-y-3">
                {loading && <div className="text-slate-500 py-8">Loading checklist…</div>}
                {!loading && data?.items?.filter(tab.filter).length === 0 && (
                  <div className="text-slate-500 py-8">Nothing here.</div>
                )}
                {!loading &&
                  data?.items?.filter(tab.filter).map((item) => (
                    <ChecklistRow key={item.key} item={item} onUpload={() => setUploadItem(item)} />
                  ))}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>

      <UploadDialog
        open={!!uploadItem}
        onOpenChange={(v) => !v && setUploadItem(null)}
        item={uploadItem}
        workerId={user?.id}
        onUploaded={load}
      />
    </AppLayout>
  );
}

function WorkerPersonalDetailsSection({ worker, onSaved }) {
  const existing = worker.personal_details || null;
  const [values, setValues] = useState({
    ...EMPTY_PERSONAL_DETAILS,
    full_legal_name: existing?.full_legal_name || worker.name || "",
    email: worker.email || "",
    mobile: existing?.mobile || "",
    home_address: existing?.home_address || "",
    date_of_birth: existing?.date_of_birth || "",
    nationality: existing?.nationality || "",
  });

  useEffect(() => {
    setValues({
      ...EMPTY_PERSONAL_DETAILS,
      full_legal_name: existing?.full_legal_name || worker.name || "",
      email: worker.email || "",
      mobile: existing?.mobile || "",
      home_address: existing?.home_address || "",
      date_of_birth: existing?.date_of_birth || "",
      nationality: existing?.nationality || "",
    });
  }, [worker.id, existing?.updated_at]);

  const complete = !!(existing && existing.full_legal_name && existing.mobile && existing.home_address && existing.date_of_birth && existing.nationality);

  return (
    <section
      className="mt-8 rounded-xl border border-slate-200 bg-white p-6"
      data-testid="personal-details-section"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="label-caps text-slate-500">Personal details</div>
          <h2 className="font-heading text-xl font-bold mt-0.5">Your profile</h2>
        </div>
        <span
          data-testid="pd-completeness"
          className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${
            complete
              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
              : "bg-amber-50 text-amber-800 border-amber-200"
          }`}
        >
          {complete ? "Complete" : "Needs completion"}
        </span>
      </div>
      <p className="text-sm text-slate-600 mt-1">
        Please keep these details up to date — your manager and reviewers use
        them to identify you on compliance records.
      </p>
      <div className="mt-6">
        <PersonalDetailsForm
          value={values}
          onChange={setValues}
          accountEmail={worker.email}
          emailEditable={false}
          workerId={worker.id}
          onSaved={onSaved}
        />
      </div>
    </section>
  );
}

function ChecklistRow({ item, onUpload }) {
  const needsAction = ["rejected", "missing", "expired", "expiring_soon"].includes(item.status);

  return (
    <div data-testid={`checklist-item-${item.key}`} className="rounded-lg border border-slate-200 bg-white p-5 card-lift">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-heading font-semibold text-slate-900">{item.title}</div>
            <span className="text-[0.65rem] uppercase tracking-widest text-slate-500 font-semibold">
              {item.category === "standard" ? "Standard" : "Role-specific"}
            </span>
            {item.essential === false && (
              <span className="text-[0.65rem] uppercase tracking-widest text-teal-700 bg-teal-50 border border-teal-100 rounded-full px-2 py-0.5 font-semibold">
                Desirable
              </span>
            )}
          </div>
          <div className="text-sm text-slate-600 mt-1">{item.description}</div>

          {item.document && (
            <div className="mt-3 flex items-center gap-2 text-xs text-slate-500 flex-wrap">
              <FileText className="h-3.5 w-3.5" />
              <span className="truncate">{item.document.original_filename || "recorded"}</span>
              <span>· uploaded {new Date(item.document.created_at).toLocaleDateString()}</span>
              {item.document?.metadata?.completed_date && (
                <>
                  <span>·</span>
                  <span>completed {formatDate(item.document.metadata.completed_date)}</span>
                </>
              )}
              {item.expiry_date && (
                <>
                  <span>·</span>
                  <CalendarClock className="h-3 w-3" />
                  <span>expires {formatDate(item.expiry_date)}</span>
                  {item.date_field === "completed_date" && (
                    <span className="text-[0.6rem] uppercase tracking-widest text-teal-700 bg-teal-50 border border-teal-100 rounded-full px-1.5 py-0.5 font-semibold">
                      auto
                    </span>
                  )}
                </>
              )}
            </div>
          )}

          {item.status === "rejected" && item.document?.rejection_reason && (
            <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 p-3 flex gap-2">
              <AlertCircle className="h-4 w-4 text-rose-600 shrink-0 mt-0.5" />
              <div className="text-sm text-rose-800">
                <div className="font-semibold">Rejected — please re-upload</div>
                <div className="mt-0.5" data-testid={`rejection-reason-${item.key}`}>{item.document.rejection_reason}</div>
              </div>
            </div>
          )}

          {(item.status === "expired" || item.status === "expiring_soon") && (
            <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 flex gap-2">
              <AlertCircle className="h-4 w-4 text-amber-700 shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900">
                {item.status === "expired"
                  ? "This document has expired. Please upload a renewed version."
                  : "This document expires within 30 days. Please upload a renewed version."}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          <StatusBadge status={item.status} testid={`item-status-${item.key}`} />
          {needsAction && (
            <Button size="sm" onClick={onUpload} data-testid={`upload-btn-${item.key}`} className="bg-teal-700 hover:bg-teal-800">
              <UploadCloud className="h-4 w-4 mr-1.5" />
              {item.status === "missing" ? "Upload" : item.status === "rejected" ? "Re-upload" : "Renew"}
            </Button>
          )}
          {item.status === "approved" && (
            <Button size="sm" variant="outline" onClick={onUpload} data-testid={`replace-btn-${item.key}`}>Replace</Button>
          )}
        </div>
      </div>
    </div>
  );
}
